<script lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, defineComponent, nextTick, PropType, ref, watch } from 'vue';
import { QSelect, ValidationRule } from 'quasar';

import IconDropdown from '@/shared/assets/icons/dropdown.vue';
import UiBottomSheet from '@/shared/ui/bottom-sheet';
import IconArrowRight from '@/shared/assets/icons/arrow-right.vue';
import UiPlaceholderEmpty from '@/shared/ui/placeholder/empty/index.vue';
import { nativeApp } from '@/shared/services/native-app';

export default defineComponent({
  name: 'UiSelect',
  components: { UiPlaceholderEmpty, IconArrowRight, UiBottomSheet, IconDropdown },
  props: {
    modelValue: {
      type: [Object, String, Number] as PropType<any>,
      default: null,
    },
    options: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    rules: {
      type: Array as PropType<ValidationRule[]>,
      default: () => [],
    },
    label: {
      type: String as PropType<undefined | string>,
      default: undefined,
    },
    hint: {
      type: String,
      default: '',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    optionContentClass: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isShowOptionLabel: {
      type: Boolean,
      default: true,
    },
    lazyRules: {
      type: Boolean,
      default: false,
    },
    mapOptions: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    borderless: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    useInput: {
      type: Boolean,
      default: false,
    },
    emitValue: {
      type: Boolean,
      default: false,
    },
    inputDebounce: {
      type: String,
      default: '0',
    },
    minimalOption: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    useChips: {
      type: Boolean,
      default: false,
    },
    hideBottomSpace: {
      type: Boolean,
      default: true,
    },
    textFirst: {
      type: Boolean,
      default: false,
    },
    loadingOnlyOnFocus: {
      type: Boolean,
      default: false,
    },
    isEnableRefreshAction: {
      type: Boolean,
      default: false,
    },
    disableRefreshActionAfterClose: {
      type: Boolean,
      default: false,
    },
    showAddAction: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'filter', 'load-more', 'add-action'],
  setup(props, { emit, expose }) {
    const { t } = useI18n();
    const selectRef = ref<QSelect>();
    const optionsListRef = ref<HTMLDivElement | null>(null);
    const filter = ref('');
    const selectedItem = ref<any>();
    const isFocused = ref(false);
    const showOptions = ref(false);
    const showNoOption = ref(!props.textFirst);
    const optionsLoading = ref(false);
    const lastLoadMoreIndex = ref<number | null>(null);
    const showIcon = computed(() => {
      if (props.disable || props.readonly) return false;
      if (!props.textFirst) return true;
      return props.modelValue && props.options?.length;
    });
    const showLoadingSkeleton = computed(() => optionsLoading.value && !props.options.length);
    const normalizedFilter = computed(() => filter.value.trim());
    const canShowAddAction = computed(
      () =>
        props.showAddAction &&
        props.useInput &&
        !props.loading &&
        !props.options.length &&
        Boolean(normalizedFilter.value)
    );
    const mappedModelValue = computed(() => {
      const modelValue = props.modelValue;

      if (!props.mapOptions) return modelValue;

      if (props.multiple) {
        return props.options.filter((opt) =>
          props.modelValue?.some((val: any) => val?.[props.optionValue] === opt?.[props.optionValue])
        );
      }

      return props.options.find((opt) => opt?.[props.optionValue] === modelValue) || null;
    });

    const fieldRules = computed(() => (showOptions.value ? [] : props.rules));

    const setShowOptions = (value: boolean) => {
      if (props.disable || props.readonly) return;
      showOptions.value = value;
      optionsLoading.value = value;
      filter.value = '';
      handleFilter('');
      setTimeout(() => {
        optionsLoading.value = !value;
      }, 500);
    };

    const selectItem = (item: any) => {
      if (props.multiple) {
        const current = Array.isArray(props.modelValue) ? [...props.modelValue] : [];

        let newValue;
        if (props.mapOptions) {
          const exists = current.some((opt) => opt?.[props.optionValue] === item?.[props.optionValue]);

          newValue = exists
            ? current.filter((opt) => opt?.[props.optionValue] !== item?.[props.optionValue])
            : [...current, item];
        } else {
          const exists = current.includes(item);

          newValue = exists ? current.filter((opt) => opt !== item) : [...current, item];
        }

        selectedItem.value = newValue; //сохраняем весь список выбранных
        return emit('update:modelValue', newValue);
      }

      const mappedValue = props.mapOptions
        ? props.options.find((opt) => opt?.[props.optionValue] === item?.[props.optionValue])
        : item;
      const emitValue = props.emitValue ? mappedValue?.[props.optionValue] : mappedValue;
      emit('update:modelValue', emitValue);
      showOptions.value = false;
    };

    const clear = () => {
      emit('update:modelValue', props.multiple ? [] : null);
    };

    const updateFilteredOptions = (callback?: () => void) => {
      callback?.();
    };

    const handleFilter = (value: string | number | null) => {
      emit('filter', String(value || ''), updateFilteredOptions);
    };

    const handleAddAction = () => {
      if (!normalizedFilter.value) return;
      emit('add-action', normalizedFilter.value);
      filter.value = '';
      showOptions.value = false;
    };

    const resetLoadMoreState = () => {
      lastLoadMoreIndex.value = null;
    };

    const emitLoadMore = () => {
      if (!showOptions.value || props.loading) return;

      const listElement = optionsListRef.value;
      if (!listElement) return;

      const lastIndex = props.options.length - 1;
      if (lastIndex < 0 || lastLoadMoreIndex.value === lastIndex) return;

      const distanceToBottom = listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight;
      const isNearBottom = listElement.scrollHeight <= listElement.clientHeight || distanceToBottom <= 48;
      if (!isNearBottom) return;

      lastLoadMoreIndex.value = lastIndex;
      emit('load-more', { index: lastIndex });
    };

    const handleOptionsScroll = () => {
      emitLoadMore();
    };

    const isActiveOption = (item: any) => {
      if (props.multiple) {
        if (props.mapOptions) {
          return props.modelValue?.some((opt: any) => opt?.[props.optionValue] === item?.[props.optionValue]);
        }

        return props.modelValue?.includes(item);
      }

      if (props.mapOptions) {
        return item?.[props.optionValue] === props.modelValue;
      }

      return JSON.stringify(item) === JSON.stringify(props.modelValue);
    };

    watch(
      () => showOptions.value,
      (value) => {
        const refreshValue = props.disableRefreshActionAfterClose ? false : !value;
        nativeApp.swipeRefreshEnabled(props.isEnableRefreshAction ? true : refreshValue);
      }
    );

    watch(
      () => props.loading,
      (value) => {
        optionsLoading.value = value;
        if (!value) {
          void nextTick(() => {
            emitLoadMore();
          });
        }
      }
    );

    watch(
      () => showOptions.value,
      (value) => {
        if (!value) {
          resetLoadMoreState();
          return;
        }

        void nextTick(() => {
          emitLoadMore();
        });
      }
    );

    watch(
      () => props.options,
      (nextOptions, previousOptions = []) => {
        if (nextOptions.length <= previousOptions.length) {
          resetLoadMoreState();
          if (optionsListRef.value) {
            optionsListRef.value.scrollTop = 0;
          }
        }

        void nextTick(() => {
          emitLoadMore();
        });
      }
    );

    expose({
      validate() {
        selectRef.value?.validate();
      },
    });

    return {
      t,
      filter,
      showIcon,
      isFocused,
      selectRef,
      fieldRules,
      showOptions,
      showNoOption,
      optionsLoading,
      optionsListRef,
      mappedModelValue,
      showLoadingSkeleton,
      clear,
      canShowAddAction,
      handleOptionsScroll,
      handleAddAction,
      handleFilter,
      selectItem,
      isActiveOption,
      normalizedFilter,
      setShowOptions,
    };
  },
});
</script>

<template>
  <div class="full-width" @click="setShowOptions(true)">
    <q-field
      ref="selectRef"
      class="o-input"
      :class="{ 'o-input_inline': inline }"
      :model-value="mappedModelValue"
      :label="label ? label : undefined"
      :loading="loading"
      :clearable="clearable"
      :outlined="!borderless"
      :borderless="borderless"
      :disable="disable"
      :rules="fieldRules"
      :lazy-rules="lazyRules"
      reactive-rules
      no-error-icon
      hide-bottom-space
      @clear="clear"
    >
      <template v-if="mappedModelValue">
        <div v-if="Array.isArray(mappedModelValue)" class="flex wrap ob-mt-md ob-mb-sm">
          <slot
            v-for="(value, index) in mappedModelValue"
            :key="index"
            :index="index"
            :tabindex="index"
            :remove="() => selectItem(value)"
            name="selected-item"
            :opt="value"
          />
        </div>

        <slot v-else name="selected-item" :opt="mappedModelValue" />
      </template>
      <template v-if="hint" #hint> {{ hint }} </template>

      <template #prepend>
        <slot name="prepend" />
      </template>

      <template #append>
        <slot name="append">
          <div class="ob-select__right">
            <slot name="append-content" />
          </div>
        </slot>
        <icon-dropdown v-if="showIcon" class="dropdown-icon ob-mx-xs" :class="{ 'dropdown-icon_popup': showOptions }" />
      </template>
    </q-field>

    <ui-bottom-sheet v-model="showOptions" middle-opening>
      <div v-if="isShowOptionLabel" class="option-title">{{ label }}</div>

      <q-input
        v-if="useInput"
        v-model="filter"
        :label="t('SHARED.SEARCH')"
        class="o-input ob-mb-md"
        :debounce="inputDebounce"
        no-error-icon
        outlined
        hide-bottom-space
        clearable
        :loading="loading"
        @update:model-value="handleFilter"
      />

      <template v-if="options.length || showLoadingSkeleton">
        <div ref="optionsListRef" class="ob-select-options" @scroll="handleOptionsScroll">
          <template v-if="showLoadingSkeleton">
            <q-skeleton
              v-for="item in 10"
              :key="item"
              height="40px"
              class="skeleton-loading select-skeleton ob-mb-md"
            />
          </template>

          <template v-else>
            <div
              v-if="$slots['before-options-body']"
              class="ob-select ob-select__content-body block"
              @click="showOptions = false"
            >
              <slot name="before-options-body"></slot>
            </div>

            <div v-for="(item, index) in options" :key="index" :class="{ disabled: item?.disable }">
              <div
                class="ob-select"
                :class="[isActiveOption(item) && 'active', optionContentClass]"
                @click="selectItem(item)"
              >
                <slot name="option" :item="item" :select-item="selectItem" :selected="isActiveOption(item)">
                  <slot name="option-left" v-bind="item">
                    <div class="ob-select__left"><slot name="option-left-content" v-bind="item" /></div>
                  </slot>
                  <div class="ob-select__content">
                    <slot name="option-content" v-bind="item">
                      <div
                        class="ob-select__content-header"
                        :class="{ 'ob-select__content-header_minimal': minimalOption }"
                      >
                        <slot name="option-header" v-bind="item" />
                      </div>
                      <div
                        class="ob-select__content-body"
                        :class="{ 'ob-select__content-body_minimal': minimalOption }"
                      >
                        <slot name="option-body" v-bind="item" :opt="item" />
                      </div>
                      <slot name="option-right" v-bind="item">
                        <div class="ob-select__right"><slot name="option-right-content" v-bind="item" /></div>
                      </slot>
                    </slot>
                  </div>
                  <div>
                    <icon-arrow-right :primary="isActiveOption(item)" class="flex ob-mr-sm" />
                  </div>
                </slot>
              </div>
            </div>

            <div
              v-if="$slots['after-options-body']"
              class="ob-select ob-select__content-body block"
              @click="showOptions = false"
            >
              <slot name="after-options-body"></slot>
            </div>

            <div v-if="loading && options.length" class="ob-select-loading-more">
              <q-spinner color="primary" size="24px" />
            </div>
          </template>
        </div>
      </template>

      <template v-else>
        <div v-if="canShowAddAction" class="ob-select-empty">
          <ui-button color="white" @click="handleAddAction">
            <span class="ob-select-empty__button">{{ t('SHARED.ACTION.ADD') }}</span>
          </ui-button>
        </div>
        <slot v-else name="no-option"><ui-placeholder-empty :label="t('SHARED.NOT_FOUND')" size="50" /></slot>
      </template>
    </ui-bottom-sheet>
  </div>
</template>

<style scoped lang="scss">
@import 'styles.module';
</style>
